export interface SpeechVoiceLike {
  lang: string;
  default: boolean;
}

export type JapaneseSpeechStatus = "idle" | "generating" | "playing" | "fallback";
export type JapaneseSpeechFailureStage = "native-request" | "native-playback" | "device-playback";
export type JapaneseSpeechOutcome = "completed" | "stopped" | "failed";

export interface ManagedSpeechPlayback {
  stop: () => void;
  finished: Promise<void>;
  /**
   * WebKit can garbage-collect a SpeechSynthesisUtterance while it is speaking.
   * Putting it here keeps a strong reference for the lifetime of the playback.
   */
  retained?: unknown;
}

export interface JapaneseSpeechFlowOptions<TNative> {
  requestNative: (signal: AbortSignal) => Promise<TNative>;
  playNative: (native: TNative) => ManagedSpeechPlayback | Promise<ManagedSpeechPlayback>;
  playDeviceFallback: () => ManagedSpeechPlayback | Promise<ManagedSpeechPlayback>;
  fallbackAfterMs: number;
  onStatus?: (status: JapaneseSpeechStatus) => void;
  onFailure?: (error: unknown, stage: JapaneseSpeechFailureStage) => void;
}

export interface JapaneseSpeechFlow {
  stop: () => void;
  finished: Promise<JapaneseSpeechOutcome>;
  readonly retained: unknown | null;
}

/**
 * A fresh speech button has no owned playback yet. Treating two empty handles
 * as the same owner makes its first tap look like a request to stop playback.
 */
export function ownsActiveSpeech<T>(active: T | null, owned: T | null): boolean {
  return owned !== null && active === owned;
}

const stoppedResult = Symbol("speech-stopped");

function stopPlayback(playback: ManagedSpeechPlayback | null) {
  if (!playback) return;
  try {
    playback.stop();
  } catch {
    // Stopping an already-ended browser playback is harmless.
  }
}

/**
 * Runs native speech through request and playback before using the device voice.
 * A slow request falls back without being aborted so the server can finish and
 * cache it. Only an explicit stop aborts the native request.
 */
export function startJapaneseSpeechFlow<TNative>(
  options: JapaneseSpeechFlowOptions<TNative>
): JapaneseSpeechFlow {
  const nativeController = new AbortController();
  let stopped = false;
  let nativeSuperseded = false;
  let activePlayback: ManagedSpeechPlayback | null = null;
  let lastStatus: JapaneseSpeechStatus | null = null;
  let resolveStopped!: (value: typeof stoppedResult) => void;
  const stoppedPromise = new Promise<typeof stoppedResult>((resolve) => {
    resolveStopped = resolve;
  });

  const emitStatus = (status: JapaneseSpeechStatus) => {
    if (lastStatus === status) return;
    lastStatus = status;
    options.onStatus?.(status);
  };
  const stop = () => {
    if (stopped) return;
    stopped = true;
    nativeController.abort();
    stopPlayback(activePlayback);
    activePlayback = null;
    resolveStopped(stoppedResult);
    emitStatus("idle");
  };
  const unlessStopped = async <T>(promise: Promise<T>): Promise<T | typeof stoppedResult> => (
    Promise.race([promise, stoppedPromise])
  );
  const preparePlayback = async (
    create: () => ManagedSpeechPlayback | Promise<ManagedSpeechPlayback>
  ): Promise<ManagedSpeechPlayback | typeof stoppedResult> => {
    let created: ManagedSpeechPlayback | Promise<ManagedSpeechPlayback>;
    try {
      created = create();
    } catch (error) {
      throw error;
    }
    const pendingPlayback = Promise.resolve(created).then((playback) => {
      if (stopped) stopPlayback(playback);
      return playback;
    });
    return unlessStopped(pendingPlayback);
  };
  const finishPlayback = async (
    playback: ManagedSpeechPlayback,
    status: Extract<JapaneseSpeechStatus, "playing" | "fallback">
  ): Promise<boolean> => {
    activePlayback = playback;
    emitStatus(status);
    try {
      const completion = await unlessStopped(playback.finished);
      return completion !== stoppedResult;
    } finally {
      if (activePlayback === playback) activePlayback = null;
    }
  };
  const play = async (
    create: () => ManagedSpeechPlayback | Promise<ManagedSpeechPlayback>,
    status: Extract<JapaneseSpeechStatus, "playing" | "fallback">
  ): Promise<boolean> => {
    const playback = await preparePlayback(create);
    if (playback === stoppedResult) return false;
    return finishPlayback(playback, status);
  };
  const useDeviceFallback = async (): Promise<JapaneseSpeechOutcome> => {
    if (stopped) return "stopped";
    try {
      const completed = await play(options.playDeviceFallback, "fallback");
      return completed ? "completed" : "stopped";
    } catch (error) {
      if (stopped) return "stopped";
      options.onFailure?.(error, "device-playback");
      return "failed";
    }
  };
  const run = async (): Promise<JapaneseSpeechOutcome> => {
    emitStatus("generating");
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    try {
      type NativeCandidate =
        | { kind: "ready"; playback: ManagedSpeechPlayback }
        | { kind: "failed"; error: unknown; stage: Extract<JapaneseSpeechFailureStage, "native-request" | "native-playback"> }
        | { kind: "ignored" };
      let nativeRequest: Promise<TNative>;
      try {
        nativeRequest = Promise.resolve(options.requestNative(nativeController.signal));
      } catch (error) {
        nativeRequest = Promise.reject(error);
      }
      const nativeCandidate = (async (): Promise<NativeCandidate> => {
        let native: TNative;
        try {
          native = await nativeRequest;
        } catch (error) {
          return { kind: "failed", error, stage: "native-request" };
        }
        if (stopped || nativeSuperseded) return { kind: "ignored" };
        try {
          const playback = await options.playNative(native);
          if (stopped || nativeSuperseded) {
            stopPlayback(playback);
            return { kind: "ignored" };
          }
          return { kind: "ready", playback };
        } catch (error) {
          return { kind: "failed", error, stage: "native-playback" };
        }
      })();
      const fallbackThreshold = new Promise<{ kind: "threshold" }>((resolve) => {
        fallbackTimer = setTimeout(
          () => resolve({ kind: "threshold" }),
          Math.max(0, options.fallbackAfterMs)
        );
      });
      const first = await Promise.race([nativeCandidate, fallbackThreshold, stoppedPromise]);
      if (first === stoppedResult || stopped) return "stopped";
      if (first.kind === "threshold") {
        nativeSuperseded = true;
        void nativeCandidate.then((late) => {
          if (late.kind === "ready") stopPlayback(late.playback);
        });
        return await useDeviceFallback();
      }
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      if (first.kind === "ignored") return stopped ? "stopped" : "failed";
      if (first.kind === "failed") {
        options.onFailure?.(first.error, first.stage);
        return await useDeviceFallback();
      }
      try {
        const completed = await finishPlayback(first.playback, "playing");
        return completed ? "completed" : "stopped";
      } catch (error) {
        if (stopped) return "stopped";
        options.onFailure?.(error, "native-playback");
        return await useDeviceFallback();
      }
    } finally {
      if (fallbackTimer !== null) clearTimeout(fallbackTimer);
      emitStatus("idle");
    }
  };

  const flow: JapaneseSpeechFlow = {
    stop,
    finished: run(),
    get retained() {
      return activePlayback?.retained ?? null;
    }
  };
  return flow;
}

const nativeSpeechWaitByMode = {
  word: 4_000,
  sentence: 8_000
} as const;

export function nativeSpeechWaitMs(mode: keyof typeof nativeSpeechWaitByMode): number {
  return nativeSpeechWaitByMode[mode];
}

export function chooseJapaneseVoice<T extends SpeechVoiceLike>(voices: T[]): T | null {
  const japanese = voices.filter((voice) => /^ja(?:-|$)/iu.test(voice.lang));
  return japanese.find((voice) => /^ja-JP$/iu.test(voice.lang) && voice.default)
    ?? japanese.find((voice) => /^ja-JP$/iu.test(voice.lang))
    ?? japanese.find((voice) => voice.default)
    ?? japanese[0]
    ?? null;
}

export function japaneseSpeechLanguage(voice: SpeechVoiceLike | null): string {
  return voice?.lang || "ja-JP";
}
